import React from 'react';

interface Invoice {
  id: number;
  numero: string;
  date_emission: string;
  patient: {
    nom: string;
    prenoms: string;
    numero_assure: string;
    nom_souscripteur: string;
  };
  assureur: {
    nom: string;
    contact: string;
    telephone: string;
    adresse: string;
  };
  montant_total: number;
  montant_assurance: number;
  montant_patient: number;
  lignes: Array<{
    treatment: string;
    quantite: number;
    prix_unitaire: number;
    montant: number;
  }>;
}

interface PrintableInvoiceTemplateProps {
  invoices: Invoice[];
  insuranceName: string;
  period: {
    start: string;
    end: string;
  };
}

const PrintableInvoiceTemplate = React.forwardRef<HTMLDivElement, PrintableInvoiceTemplateProps>(
  ({ invoices, insuranceName, period }, ref) => {
    const totalAmount = invoices.reduce((sum, inv) => sum + inv.montant_assurance, 0);
    const totalInvoices = invoices.length;

    return (
      <div ref={ref} className="print-container bg-white">
        <style>{`
          @media print {
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-container {
              width: 100%;
              max-width: 210mm;
              margin: 0 auto;
            }
            .page-break {
              page-break-after: always;
            }
            .no-print {
              display: none !important;
            }
          }
          .print-container {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #1a1a1a;
            max-width: 210mm;
            margin: 0 auto;
            padding: 20px;
          }
          .print-header {
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .print-logo {
            max-height: 80px;
            max-width: 200px;
          }
          .print-title {
            font-size: 24px;
            font-weight: bold;
            color: #2563eb;
            margin-bottom: 5px;
          }
          .print-subtitle {
            font-size: 14px;
            color: #666;
          }
          .print-info-box {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 20px;
          }
          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .print-table th {
            background: #2563eb;
            color: white;
            padding: 12px 8px;
            text-align: left;
            font-size: 12px;
            font-weight: 600;
          }
          .print-table td {
            padding: 10px 8px;
            border-bottom: 1px solid #e2e8f0;
            font-size: 11px;
          }
          .print-table tr:hover {
            background: #f8fafc;
          }
          .print-summary {
            background: #f0f9ff;
            border: 2px solid #2563eb;
            border-radius: 8px;
            padding: 20px;
            margin-top: 30px;
          }
          .print-footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e2e8f0;
            text-align: center;
            font-size: 11px;
            color: #666;
          }
          .clinic-info {
            font-size: 11px;
            color: #666;
            line-height: 1.6;
          }
        `}</style>

        {/* Header with Logo */}
        <div className="print-header">
          <div className="flex justify-between items-start">
            <div>
              <img src="/Logo - SONRISA.png" alt="Cabinet SONRISA" className="print-logo mb-3" />
              <div className="clinic-info">
                <div className="font-semibold text-gray-900">Cabinet Dentaire SONRISA</div>
                <div>Conakry, Guinée</div>
                <div>Tél: +224 620 46 63 29</div>
                <div>Email: contact@cabinet-sonrisa.clinic</div>
              </div>
            </div>
            <div className="text-right">
              <div className="print-title">BORDEREAU D'ASSURANCE</div>
              <div className="print-subtitle">
                Période: {new Date(period.start).toLocaleDateString('fr-FR')} au{' '}
                {new Date(period.end).toLocaleDateString('fr-FR')}
              </div>
              <div className="mt-2 text-sm">
                <div className="font-semibold">Date d'émission:</div>
                <div>{new Date().toLocaleDateString('fr-FR')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Insurance Information */}
        <div className="print-info-box">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-semibold text-gray-700 mb-2">Assureur:</div>
              <div className="text-lg font-bold text-blue-700">{insuranceName}</div>
            </div>
            <div className="text-right">
              <div className="font-semibold text-gray-700 mb-2">Résumé:</div>
              <div className="text-sm">
                <div>Nombre de factures: <span className="font-bold">{totalInvoices}</span></div>
                <div>Montant total: <span className="font-bold">{totalAmount.toLocaleString()} GNF</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Invoices Table */}
        <table className="print-table">
          <thead>
            <tr>
              <th>N° Facture</th>
              <th>Date</th>
              <th>Patient</th>
              <th>N° Assuré</th>
              <th>Souscripteur</th>
              <th className="text-right">Montant Total</th>
              <th className="text-right">Part Assurance</th>
              <th className="text-right">Part Patient</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.id}>
                <td className="font-semibold">{invoice.numero}</td>
                <td>{new Date(invoice.date_emission).toLocaleDateString('fr-FR')}</td>
                <td>{invoice.patient.nom} {invoice.patient.prenoms}</td>
                <td>{invoice.patient.numero_assure}</td>
                <td className="text-xs">{invoice.patient.nom_souscripteur || '-'}</td>
                <td className="text-right">{invoice.montant_total.toLocaleString()}</td>
                <td className="text-right font-semibold text-blue-700">
                  {invoice.montant_assurance.toLocaleString()}
                </td>
                <td className="text-right">{invoice.montant_patient.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Summary Box */}
        <div className="print-summary">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-lg font-semibold text-gray-700">Montant Total à Payer:</div>
              <div className="text-sm text-gray-600 mt-1">
                {totalInvoices} facture{totalInvoices > 1 ? 's' : ''} incluse{totalInvoices > 1 ? 's' : ''}
              </div>
            </div>
            <div className="text-3xl font-bold text-blue-700">
              {totalAmount.toLocaleString()} GNF
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="grid grid-cols-2 gap-8 mt-8 mb-8">
          <div>
            <div className="font-semibold mb-8">Pour le Cabinet SONRISA</div>
            <div className="border-t border-gray-400 pt-2 text-sm text-gray-600">
              Signature et cachet
            </div>
          </div>
          <div>
            <div className="font-semibold mb-8">Pour l'Assurance {insuranceName}</div>
            <div className="border-t border-gray-400 pt-2 text-sm text-gray-600">
              Signature et cachet
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="print-footer">
          <div className="mb-2">
            Ce document a été généré électroniquement le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}
          </div>
          <div className="text-xs">
            Cabinet Dentaire SONRISA - Conakry, Guinée | www.cabinet-sonrisa.clinic
          </div>
        </div>
      </div>
    );
  }
);

PrintableInvoiceTemplate.displayName = 'PrintableInvoiceTemplate';

export default PrintableInvoiceTemplate;
